import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCommHistoryV1Service, serviceName } from '../service';
import { ListHistoryIO, ServiceQueries } from '../types';
import {
  UseCommHistoryV1InfiniteQueryEndpointArgs,
  useCommHistoryV1InfiniteQuery,
} from './use-comm-history-v1-infinite-query';

type ListHistoryPageParams = {
  skip?: string;
};

const DEFAULT_LIST_HISTORY_LIMIT = 25;

/**
 * A hook that returns an infinite query for the `ListHistory` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListHistoryInfiniteQuery = <E = unknown, D = ListHistoryIO['output']>({
  options,
  request,
  ...args
}: UseCommHistoryV1InfiniteQueryEndpointArgs<'ListHistory', E, D, ListHistoryPageParams>) =>
  useCommHistoryV1InfiniteQuery<'ListHistory', E, D, ListHistoryPageParams>({
    endpointName: 'ListHistory',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListHistoryIO['input'] = {
        ...request,
      };

      if (pageParam?.skip) {
        requestWithPageParams.skip = pageParam.skip;
      }

      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage) => {
        const pageSize = request.limit ?? DEFAULT_LIST_HISTORY_LIMIT;
        const currentPageSize = lastPage.entries?.length ?? 0;

        if (currentPageSize < pageSize || !lastPage.nextSkip) {
          return undefined;
        }

        return {
          skip: lastPage.nextSkip,
        };
      },
      getPreviousPageParam: () => {
        return undefined;
      },
    },
  });

/**
 * A hook that returns a function to fetch the query for the `ListHistory` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListHistory = () => {
  const queryClient = useQueryClient();

  const fetchListHistory = async <E = unknown, D = ListHistoryIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCommHistoryV1InfiniteQueryEndpointArgs<'ListHistory', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      ListHistoryIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListHistory'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListHistoryIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListHistory'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListHistory'>({
          serviceName,
          endpointName: 'ListHistory',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCommHistoryV1Service.ListHistory(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchListHistory;
};
